import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { updateStudent, getDisplayPreferencesDefaults } from "../lib/api";
import {
  Settings2,
  Type,
  Eye,
  BookOpen,
  Save,
  Loader,
  RotateCcw,
} from "lucide-react";

// Font options
const FONT_OPTIONS = [
  { value: "Nunito", label: "Nunito (Standard)" },
  { value: "OpenDyslexic", label: "OpenDyslexic (Dyslexie)" },
  { value: "Comic Sans MS", label: "Comic Sans (Lisible)" },
  { value: "Lexie Readable", label: "Lexie (Accessible)" },
  { value: "Fredoka", label: "Fredoka (Enfant)" },
  { value: "Georgia", label: "Georgia (Serif)" },
  { value: "Caveat", label: "Caveat (Manuscrit)" },
];

// Reading aid levels
const READING_AID_LEVELS = [
  { value: "none", label: "Aucune aide" },
  { value: "light", label: "Aide legere" },
  { value: "medium", label: "Aide moyenne" },
  { value: "full", label: "Aide complete" },
];

// Content densities
const CONTENT_DENSITIES = [
  { value: "compact", label: "Compact" },
  { value: "normal", label: "Normal" },
  { value: "spacious", label: "Aere" },
];

// Default preferences
const DEFAULT_PREFS = {
  font_family: "Nunito",
  font_size: 18,
  line_height: 1.6,
  word_spacing: 0.2,
  letter_spacing: 0.02,
  text_alignment: "left",
  show_colored_syllables: true,
  show_silent_letters: true,
  show_grapheme_highlight: false,
  show_line_alternation: false,
  high_contrast: false,
  seyes_by_default: false,
  content_density: "normal",
  reading_aid_level: "medium",
};

/**
 * StudentDisplayPreferencesDialog - Modal to configure student display preferences
 */
export function StudentDisplayPreferencesDialog({ 
  open, 
  onOpenChange, 
  student, 
  onSave 
}) {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (student?.display_preferences) {
      setPrefs({ ...DEFAULT_PREFS, ...student.display_preferences });
    } else {
      setPrefs(DEFAULT_PREFS);
    }
    setHasChanges(false);
  }, [student, open]);

  const handleChange = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleReset = () => {
    setPrefs(DEFAULT_PREFS);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!student) return;
    
    setSaving(true);
    try {
      await updateStudent(student.id, { display_preferences: prefs });
      onSave?.(prefs);
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving preferences:", err);
      alert("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-indigo-500" />
            Preferences d'affichage - {student.student_name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <Accordion type="multiple" defaultValue={["typography", "reading", "display"]} className="space-y-2">
            {/* Typography Section */}
            <AccordionItem value="typography" className="border rounded-lg px-3">
              <AccordionTrigger className="py-3">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Type className="w-4 h-4" />
                  Typographie
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4 space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Police de caracteres</Label>
                  <Select
                    value={prefs.font_family}
                    onValueChange={(v) => handleChange("font_family", v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((font) => (
                        <SelectItem key={font.value} value={font.value}>
                          <span style={{ fontFamily: font.value }}>{font.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">
                    Taille de police ({prefs.font_size}px)
                  </Label>
                  <Slider
                    value={[prefs.font_size]}
                    onValueChange={([v]) => handleChange("font_size", v)}
                    min={12}
                    max={32}
                    step={1}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">
                    Interligne ({prefs.line_height})
                  </Label>
                  <Slider
                    value={[prefs.line_height]}
                    onValueChange={([v]) => handleChange("line_height", v)}
                    min={1}
                    max={3}
                    step={0.1}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">
                    Espacement des lettres ({prefs.letter_spacing})
                  </Label>
                  <Slider
                    value={[prefs.letter_spacing]}
                    onValueChange={([v]) => handleChange("letter_spacing", v)}
                    min={0}
                    max={0.3}
                    step={0.01}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">
                    Espacement des mots ({prefs.word_spacing})
                  </Label>
                  <Slider
                    value={[prefs.word_spacing]}
                    onValueChange={([v]) => handleChange("word_spacing", v)}
                    min={0}
                    max={0.5}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Reading Aids Section */}
            <AccordionItem value="reading" className="border rounded-lg px-3 border-green-200 bg-green-50/50">
              <AccordionTrigger className="py-3">
                <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                  <BookOpen className="w-4 h-4" />
                  Aides a la lecture
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Syllabes colorees</Label>
                    <p className="text-xs text-slate-400">Colore les syllabes du texte</p>
                  </div>
                  <Switch
                    checked={prefs.show_colored_syllables}
                    onCheckedChange={(v) => handleChange("show_colored_syllables", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Lettres muettes</Label>
                    <p className="text-xs text-slate-400">Met en evidence les lettres muettes</p>
                  </div>
                  <Switch
                    checked={prefs.show_silent_letters}
                    onCheckedChange={(v) => handleChange("show_silent_letters", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Graphemes complexes</Label>
                    <p className="text-xs text-slate-400">Surligne les sons complexes (ou, an, etc.)</p>
                  </div>
                  <Switch
                    checked={prefs.show_grapheme_highlight}
                    onCheckedChange={(v) => handleChange("show_grapheme_highlight", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Alternance des lignes</Label>
                    <p className="text-xs text-slate-400">Colore les lignes en alternance</p>
                  </div>
                  <Switch
                    checked={prefs.show_line_alternation}
                    onCheckedChange={(v) => handleChange("show_line_alternation", v)}
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Niveau d'aide global</Label>
                  <Select
                    value={prefs.reading_aid_level}
                    onValueChange={(v) => handleChange("reading_aid_level", v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {READING_AID_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Display Section */}
            <AccordionItem value="display" className="border rounded-lg px-3">
              <AccordionTrigger className="py-3">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Eye className="w-4 h-4" />
                  Affichage
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Contraste eleve</Label>
                    <p className="text-xs text-slate-400">Texte noir sur blanc pur</p>
                  </div>
                  <Switch
                    checked={prefs.high_contrast}
                    onCheckedChange={(v) => handleChange("high_contrast", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-700">Lignes Seyes par defaut</Label>
                    <p className="text-xs text-slate-400">Pour les zones d'ecriture</p>
                  </div>
                  <Switch
                    checked={prefs.seyes_by_default}
                    onCheckedChange={(v) => handleChange("seyes_by_default", v)}
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Densite du contenu</Label>
                  <Select
                    value={prefs.content_density}
                    onValueChange={(v) => handleChange("content_density", v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_DENSITIES.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Preview */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <Label className="text-xs text-slate-500 mb-2 block">Apercu</Label>
            <div
              className={`p-4 bg-white rounded-lg border ${prefs.high_contrast ? "border-black" : "border-slate-200"}`}
              style={{
                fontFamily: prefs.font_family,
                fontSize: `${prefs.font_size}px`,
                lineHeight: prefs.line_height,
                letterSpacing: `${prefs.letter_spacing}em`,
                wordSpacing: `${prefs.word_spacing}em`,
              }}
            >
              <p>Le petit chat dort sur le tapis.</p>
              <p className="mt-2">Il fait beau aujourd'hui !</p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reinitialiser
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
